import { Box, Tooltip } from '@mui/material';
import type { ReactNode } from 'react';
import { useContext } from 'react';
import Ellipsis from 'react-ellipsis-component';
import { useInView } from 'react-intersection-observer';
import xxhash from 'xxhashjs';

import { RowOffsetContext, useSwimlaneRootProps } from '@/components/ui/swimlane/context';
import { GRID_GAP, ROW_HEIGHT_MIN } from '../../constants';

interface IHorizontalTitle {
  bottomEntry: IntersectionObserverEntry | undefined;
  children: ReactNode;
  columnHeaderOffset: number;
  menuHeight: number;
}

export function HorizontalTitle({
  bottomEntry,
  children,
  columnHeaderOffset,
  menuHeight,
}: IHorizontalTitle) {
  const { container } = useContext(RowOffsetContext);
  const { ref: topRef, entry: topEntry } = useInView({ root: container });
  const { rowHeadersWidth } = useSwimlaneRootProps();

  const sl_container_rect = container?.getBoundingClientRect();
  const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
  const scrollOffset = container
    ? Math.max(Math.min(GRID_GAP.value - container.scrollTop, GRID_GAP.value), 0)
    : 0;

  const headerHeight =
    Math.min(
      vh,
      sl_container_rect?.bottom ?? 0,
      bottomEntry?.target.getBoundingClientRect().bottom ?? 0,
    ) -
      Math.max(
        (sl_container_rect?.top ?? 0) + columnHeaderOffset + scrollOffset + menuHeight,
        topEntry?.target.getBoundingClientRect().top ?? 0,
      ) || ROW_HEIGHT_MIN.value;

  // <Ellipsis reflowOnResize={true} /> fails to calculate height properly, so we use a fast hash to create a composite react key
  const key = xxhash
    .h32(`${rowHeadersWidth}-${typeof children === 'string' ? children : ''}`, 0)
    .toString(16);

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <span ref={topRef} style={{ height: '1px', width: '100%' }} />
      </Box>
      <Tooltip title={typeof children === 'string' ? children : ''} followCursor>
        <Box
          component="div"
          data-testid="horizontal"
          style={{
            overflow: 'hidden',
            wordWrap: 'break-word',
            fontSize: '0.875rem',
            lineHeight: '20px',
            fontWeight: 600,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            textAlign: 'center',
          }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            px: 3,
            pb: 3,
          }}
        >
          {typeof children === 'string' ? (
            <Ellipsis
              className="fs-exclude"
              data-dd-action-name="swimlane label - redacted"
              key={key}
              ellipsis={true}
              text={children}
              maxHeight={headerHeight - 16}
              reflowOnResize={false}
            />
          ) : (
            children
          )}
        </Box>
      </Tooltip>
    </>
  );
}
